import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { Button, Grid } from '@mui/material';
import Table from '../Table';
import { useColumnDefinition } from './components/column-definitions';
import DeferredPaymentForm from './components/form';
import { DeferredPaymentProps } from './components/models';
import { RetrieveDbHookProps, useRetrieveDbService } from '../../hooks/use-retrieve-db-service';
import { BookedRetrieve, Retrieve } from '@tff/types/tff';
import { isTTSBooking } from '../../util/booking-type-utils';
import { MUIDataTableOptions, MUIDataTableState } from 'mui-datatables';
import ChangeDialog, { ChangeForm } from './components/dialog';
import { UpdateRetrieveDbProps, useUpdateRetrieveDb } from '../../hooks/use-update-retrieve-db';
import { toast } from 'react-toastify';
import moment from 'moment';
import { UpdateResultDialog } from './UpdateResultDialog';
import { usePnrUpdate } from '../../hooks/use-pnr-update';

type SearchValues = DeferredPaymentProps & {
  pageSize?: number;
  cursor?: string;
};

type CustomToolbarProps = {
  selectedRows: number[];
};

const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

const filterData = (matchObj: Partial<DeferredPaymentProps>, data: Retrieve[]) => {
  const matchEntries = Object.entries(matchObj);
  return data.filter(item => {
    if (item.status === 'CANCELLED') return false;

    let ret = true;
    const outDeparture = 'outDeparture' in item && item.outDeparture;
    const ticketTimeLimit = 'ticketTimeLimit' in item && item.ticketTimeLimit;

    return matchEntries.every(([key, val]) => {
      if (key === 'departureDateFrom') {
        ret = outDeparture ? new Date(outDeparture) >= new Date(val) : false;
      } else if (key === 'lastTicketingDate') {
        ret = ticketTimeLimit ? new Date(ticketTimeLimit) >= new Date(val) : false;
      } else if (key === 'departureDateTo') {
        ret = outDeparture ? new Date(outDeparture) < new Date(val) : false;
      }

      return ret;
    });
  });
};

const DeferredPayment = () => {
  const [retrieveItems, setRetrieveItems] = useState<BookedRetrieve[]>([]);
  const [retrieveDbServiceProps, setRetrieveDbServiceProps] = useState<RetrieveDbHookProps | undefined>({});
  const [updateRetrieveDbProps, setUpdateRetrieveDbProps] = useState<UpdateRetrieveDbProps | undefined>(undefined);
  const [searchValues, setSearchValues] = useState<SearchValues | undefined>(undefined);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(500);
  const [cursorPerPage, setCursorPerPage] = useState<Record<number, string>>({ 0: undefined });
  const [nextCursor, setNextCursor] = useState<string>();
  const [total, setTotal] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<string[][]>([]);
  const [sortOrder, setSortOrder] = useState<{ name: string; direction: 'asc' | 'desc' } | null>(null);

  const { resetRetrieveDbState, fetchedRetrieveDbItem, fetchedRetrieveDbItems } =
    useRetrieveDbService(retrieveDbServiceProps);

  const { updateRetrieveResponse, updateRetrieveDbStatus, resetUpdateRetrieveDbState } =
    useUpdateRetrieveDb(updateRetrieveDbProps);

  const { dialogOpen, updateResponse, handlePnrUpdateResponse, closeDialog } = usePnrUpdate();

  const columnDefinition = useColumnDefinition();

  useEffect(() => {
    if (updateRetrieveDbStatus === 'FINISH' && updateRetrieveResponse) {
      handlePnrUpdateResponse(updateRetrieveResponse);

      // Update local table data for successful PNRs
      if (updateRetrieveResponse.UpdatedPnrs?.length > 0 && updateRetrieveDbProps) {
        setRetrieveItems(prevItems =>
          prevItems.map(item => {
            if (updateRetrieveResponse.UpdatedPnrs?.includes(item.tfmPnr)) {
              return { ...item, ticketTimeLimit: updateRetrieveDbProps.ticketTimeLimit };
            }
            return item;
          }),
        );
      }
    } else if (updateRetrieveDbStatus === 'FAILURE') {
      toast.error('SOMETHING went wrong! Please try again');
    }
  }, [updateRetrieveDbStatus, updateRetrieveResponse, updateRetrieveDbProps]);

  useEffect(() => {
    if ('total' in fetchedRetrieveDbItems && fetchedRetrieveDbItems.total) setTotal(fetchedRetrieveDbItems.total);

    if (fetchedRetrieveDbItem) {
      if (isTTSBooking(fetchedRetrieveDbItem)) {
        setRetrieveItems([]); // filter out TTS bookings - should not be displayed
      } else {
        setRetrieveItems([fetchedRetrieveDbItem as BookedRetrieve]);
      }
    } else if ('items' in fetchedRetrieveDbItems) {
      setRetrieveItems(fetchedRetrieveDbItems.items as BookedRetrieve[]);
      setNextCursor(fetchedRetrieveDbItems.nextCursor);
      if (searchValues?.product) {
        if (searchValues.bookingSource?.length === 0) {
          delete searchValues.bookingSource;
        }
        if (searchValues.outCabinClasses?.length === 0) {
          delete searchValues.outCabinClasses;
        }

        const filteredList = filterData(searchValues, fetchedRetrieveDbItems.items);
        setRetrieveItems([...(filteredList as BookedRetrieve[])]);
      }
    }
  }, [fetchedRetrieveDbItems, fetchedRetrieveDbItem, searchValues]);

  const handleCustomToolbar = (selectedRows: number[]) => {
    setSelectedRows(selectedRows);
    resetUpdateRetrieveDbState();
    setOpenDialog(true);
  };

  const CustomToolbar: React.FC<CustomToolbarProps> = ({ selectedRows }) => {
    return (
      <div style={{ margin: 5 }}>
        <Button color="primary" variant="contained" onClick={() => handleCustomToolbar(selectedRows)}>
          Change Ticket Time Limit
        </Button>
      </div>
    );
  };

  const tableOptions: MUIDataTableOptions = {
    selectableRows: 'multiple',
    selectableRowsHeader: true,
    page,
    rowsPerPage,
    rowsPerPageOptions: [10, 50, 100, 500],
    count: total,
    serverSide: true,
    setRowProps(row) {
      // Check search
      if (searchText) {
        const lowerCaseSearch = searchText.toLowerCase();
        const searchMatches = row.some(cell => {
          const cellText = cell?.props?.children?.props?.children || cell?.props?.children || cell || '';
          return String(cellText).toLowerCase().includes(lowerCaseSearch);
        });
        if (!searchMatches) {
          return { style: { display: 'none' } };
        }
      }

      // Check filters
      const filterMatches = columnFilters.every((filterList, colIndex) => {
        if (!filterList || filterList.length === 0) return true;
        const cellText =
          row[colIndex]?.props?.children?.props?.children || row[colIndex]?.props?.children || row[colIndex] || '';
        return filterList.some(filter => String(cellText).toLowerCase().includes(filter.toLowerCase()));
      });

      return {
        style: {
          display: filterMatches ? 'table-row' : 'none',
        },
      };
    },
    onFilterChange: (_, filterList) => {
      setColumnFilters(filterList);
    },
    onSearchChange(searchText: string | null) {
      setSearchText(searchText);
    },
    customToolbarSelect: selectedRows => {
      return <CustomToolbar selectedRows={selectedRows.data.map(d => d.dataIndex)} />;
    },
    onTableChange(action: string, tableState: MUIDataTableState) {
      if (action === 'changePage') {
        const newPage = tableState.page;
        const newDirection = newPage > page ? 'next' : 'previous';
        setPage(newPage);

        if (newDirection === 'next') {
          handleSearch({ ...searchValues, pageSize: rowsPerPage, cursor: nextCursor });
          if (nextCursor && newDirection === 'next') {
            setCursorPerPage(prev => ({ ...prev, [newPage]: nextCursor }));
          }
        } else handleSearch({ ...searchValues, pageSize: rowsPerPage, cursor: cursorPerPage[newPage] });
      } else if (action === 'changeRowsPerPage') {
        const newRowsPerPage = tableState.rowsPerPage;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        setCursorPerPage({ 0: undefined });
        handleSearch({ ...searchValues, pageSize: newRowsPerPage, cursor: undefined });
      } else if (action === 'sort') {
        const { name, direction } = tableState.sortOrder;
        setSortOrder({ name, direction });
      }
    },
  };

  const sortedItems = useMemo(() => {
    if (!sortOrder) return retrieveItems;

    return [...retrieveItems].sort((a, b) => {
      const aVal = getNestedValue(a, sortOrder.name);
      const bVal = getNestedValue(b, sortOrder.name);

      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return sortOrder.direction === 'asc' ? comparison : -comparison;
    });
  }, [retrieveItems, sortOrder]);

  const onTicketTimeChange = (values: ChangeForm) => {
    let { ticketTimeLimit } = values;

    if (ticketTimeLimit) {
      const utcTime = moment(ticketTimeLimit).toISOString(true);

      const retrievesToChange: Retrieve[] = [];
      selectedRows.forEach(row => retrievesToChange.push(retrieveItems[row]));

      setUpdateRetrieveDbProps({
        itemsToUpdate: retrievesToChange,
        ticketTimeLimit: utcTime,
      });
      setOpenDialog(false);
    }
  };

  const handleSearch = (searchValues: SearchValues) => {
    const { tfmPnr, product, pageSize, cursor } = searchValues;
    setSearchValues(searchValues);
    resetRetrieveDbState();
    if (tfmPnr) {
      setRetrieveDbServiceProps({ tfmPnr });
    } else if (product) {
      setRetrieveDbServiceProps({ product, pageSize: pageSize || tableOptions.rowsPerPage, cursor });
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <DeferredPaymentForm onSubmit={handleSearch} />
      </Grid>
      <Grid item xs={12}>
        <Table
          data={sortedItems}
          columnsData={columnDefinition}
          tableId="deferredPaymentTable"
          tableName={'Deferred Payments'}
          otherOptions={tableOptions}
        />
      </Grid>
      {openDialog && (
        <ChangeDialog open={openDialog} onCancel={() => setOpenDialog(false)} onConfirm={onTicketTimeChange} />
      )}
      {updateResponse && <UpdateResultDialog open={dialogOpen} onClose={closeDialog} response={updateResponse} />}
    </Grid>
  );
};

export default DeferredPayment;
